import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAwsAccountService } from '../services/awsAccountService';
import { awsAccountKeys } from './queryKeys';

const message = (err: unknown, fallback: string): string => (err instanceof Error ? err.message : fallback);

/**
 * The writes on one account's baselines: switch an optional baseline, run the
 * account now, and the two decisions (OpsPilot installs the baselines, the
 * customer controls admin access), each on its own action. Each returns before
 * the account converges, so all invalidate the status read rather than waiting
 * for its poll.
 */
export function useAccountBaselineActions(customerId: string, awsAccountId: string) {
  const service = useAwsAccountService();
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: awsAccountKeys.baselineStatus(customerId) });

  const setOptionalMutation = useMutation({
    mutationFn: ({ baseline, enabled }: { baseline: string; enabled: boolean }) =>
      service.setAccountOptionalBaseline(customerId, awsAccountId, baseline, enabled),
    onSuccess: invalidate,
  });

  const runMutation = useMutation({
    mutationFn: () => service.runAccountBaseline(customerId, awsAccountId),
    onSuccess: invalidate,
  });

  const setManagedMutation = useMutation({
    mutationFn: (managed: boolean) => service.setAccountBaselineManaged(customerId, awsAccountId, managed),
    onSuccess: invalidate,
  });

  const setBreakGlassMutation = useMutation({
    mutationFn: (required: boolean) => service.setAccountBreakGlass(customerId, awsAccountId, required),
    onSuccess: invalidate,
  });

  const mutations = [setOptionalMutation, runMutation, setManagedMutation, setBreakGlassMutation];
  const failures: Array<[unknown, string]> = [
    [setOptionalMutation.error, 'Could not switch the baseline'],
    [runMutation.error, 'Could not start the run'],
    [setManagedMutation.error, 'Could not change who installs the baselines'],
    [setBreakGlassMutation.error, 'Could not change who controls admin access'],
  ];
  const failed = failures.find(([err]) => err);

  return {
    setOptional: (baseline: string, enabled: boolean) => setOptionalMutation.mutate({ baseline, enabled }),
    runNow: () => runMutation.mutate(),
    setManaged: (managed: boolean) => setManagedMutation.mutate(managed),
    setBreakGlass: (required: boolean) => setBreakGlassMutation.mutate(required),
    busy: mutations.some((m) => m.isPending),
    error: failed ? message(failed[0], failed[1]) : null,
    clearError: () => mutations.forEach((m) => m.reset()),
  };
}
